var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var AutoTestSuite_1;
/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import moment from 'moment';
import { Left, Right } from 'purify-ts/Either';
import * as api from '@codegrade/api';
import { Maybe, Nothing } from '@codegrade/maybe';
import client from '@/client';
import { store } from '@/store';
import { AutoTestStore } from '@/store/modules/autotest';
import * as utils from '@/utils';
import { getErrorMessage, getUniqueId, ifExpr, immutable } from '@/utils';
import { BaseAutoTestStep, createAutoTestStepFromServerData, } from './step';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class AutoTestSuiteSaveError extends Error {
    messages;
    /** Public tag to make a vue template easier to work with. */
    isAutoTestSuiteSaveError = true;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(msg, messages) {
        super(msg);
        this.messages = messages;
    }
}
let AutoTestSuite = AutoTestSuite_1 = class AutoTestSuite {
    id;
    networkDisabled;
    submissionInfo;
    set;
    _commandTimeLimit;
    rubricRowId;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    trackingId;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    steps;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(id, networkDisabled, submissionInfo, set, steps, _commandTimeLimit, rubricRowId, trackingId) {
        this.id = id;
        this.networkDisabled = networkDisabled;
        this.submissionInfo = submissionInfo;
        this.set = set;
        this._commandTimeLimit = _commandTimeLimit;
        this.rubricRowId = rubricRowId;
        if (steps.mixed) {
            this.steps = Object.freeze(steps.steps.map(s => {
                if (s instanceof BaseAutoTestStep) {
                    return s;
                }
                return createAutoTestStepFromServerData(s, this);
            }));
        }
        else {
            this.steps = steps.steps;
        }
        this.trackingId = trackingId.orDefaultLazy(getUniqueId);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(data, set, trackingId = Nothing) {
        return new AutoTestSuite_1(Maybe.of(data.id), data.networkDisabled, data.submissionInfo, set, { mixed: true, steps: data.steps }, data.commandTimeLimit, Maybe.of(data.rubricRow.id), trackingId);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get pointsPossible() {
        return utils.sumMap(this.steps, utils.attr('weight'));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static createEmpty(set) {
        return new AutoTestSuite_1(Nothing, true, false, set, { mixed: false, steps: [] }, Nothing, Nothing, Nothing);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get autoTestId() {
        return this.set.autoTestId;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getCommandTimeLimit() {
        return this._commandTimeLimit.orDefaultLazy(() => 
        // Must be the same as `MAX_TIME_COMMAND` in `psef/auto_test/settings.py`.
        moment.duration(120, 's'));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    update(data) {
        return new AutoTestSuite_1(this.id, data.networkDisabled ?? this.networkDisabled, data.submissionInfo ?? this.submissionInfo, this.set, { mixed: false, steps: data.steps ?? this.steps }, data.commandTimeLimit ?? this._commandTimeLimit, data.rubricRowId ?? this.rubricRowId, Maybe.of(this.trackingId));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    copy() {
        this.update({});
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    addStep(step) {
        return this.update({ steps: this.steps.concat(step) });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    removeStep(step) {
        return this.update({ steps: this.steps.filter(s => !s.equals(step)) });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    updateStep(newStep) {
        return this.update({
            steps: this.steps.map(step => {
                if (step.equals(newStep)) {
                    return newStep;
                }
                return step;
            }),
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isEmpty() {
        return this.steps.length === 0;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get url() {
        return `/api/v1/auto_tests/${this.autoTestId}/sets/${this.set.id}/suites/`;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async save() {
        return await this.getDataToSave().caseOf({
            Right: async (data) => {
                try {
                    const res = await client.p.autoTest.updateSuite({
                        autoTestId: this.autoTestId,
                        setId: this.set.id,
                        requestBody: data,
                    });
                    return res.map(result => AutoTestSuite_1.fromServerData(result, this.set, Maybe.of(this.trackingId)));
                }
                catch (err) {
                    if (err instanceof api.ApiResultError &&
                        err.error instanceof api.AutoTestStepValidationException) {
                        throw new AutoTestSuiteSaveError('The category is not valid.', {
                            general: [],
                            steps: [this._parseValidationError(err.error)],
                        });
                    }
                    throw err;
                }
            },
            Left: async (errors) => {
                throw new AutoTestSuiteSaveError('The category is not valid.', errors);
            },
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _parseValidationError(err) {
        const step = this.steps[err.stepIdx];
        const errors = [];
        if (err instanceof api.InvalidIOCasesException) {
            errors.push(...err.invalidCases.map(([idx, msg]) => {
                const substep = step.data.inputs[idx];
                return `Input ${substep.name}: ${msg}`;
            }));
        }
        else {
            errors.push(getErrorMessage(err));
        }
        return { step, errors, index: err.stepIdx };
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    delete() {
        return client.maybeRequest(this.id, suiteId => client.autoTest.deleteSuite({
            testId: this.autoTestId,
            setId: this.set.id,
            suiteId,
        }));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get rubricRow() {
        const getter = AutoTestStore.getTest()(this.autoTestId)
            .map(utils.attr('assignmentId'))
            .chain(utils.getItem(store.state.rubrics.rubrics))
            .map(rubric => utils.getItem(rubric.rowsById));
        return this.rubricRowId.ap(getter).join();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getDataToSave() {
        const generalStepError = ifExpr(this.steps.length === 0, () => Left('You should have at least one step.'), () => Right(null));
        let totalWeight = 0;
        const stepData = utils.eitherUtils.sequence(this.steps.map((s, i) => s
            .getDataToSave({
            totalWeight,
            remainingSteps: this.steps.length - i - 1,
        })
            .bimap(err => ({
            index: i,
            step: s,
            errors: err,
        }), data => {
            totalWeight += data.weight;
            return data;
        })));
        const totalWeightError = utils.eitherUtils.fromFalsy(totalWeight > 0, 'At least one step must have a positive weight.');
        const rubricRowId = this.rubricRowId.toEither('You should select a rubric category for this test category.');
        return utils.eitherUtils
            .and(stepData, rubricRowId, generalStepError, totalWeightError)
            .bimap(([stepErrors, ...generalErrors]) => ({
            steps: stepErrors.caseOf({
                Just: (e) => e,
                Nothing: () => [],
            }),
            general: Maybe.catMaybes(generalErrors),
        }), ([steps, rowId]) => ({
            id: this.id.extract(),
            steps,
            rubricRowId: rowId,
            commandTimeLimit: this.getCommandTimeLimit(),
            networkDisabled: this.networkDisabled,
            submissionInfo: this.submissionInfo,
        }));
    }
};
AutoTestSuite = AutoTestSuite_1 = __decorate([
    immutable
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTestSuite);
export { AutoTestSuite };
